import { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { faAngleDown, faCircle, faLockKeyhole } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { darkColors, StyledFlex, SwitchThemeContext, theme } from '@tryhackme/thm-ui-components';
import { CSSTransition } from 'react-transition-group';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ThemeProvider } from 'styled-components';
import { useAppSelector } from 'src/app/hooks';
import { ThemeColorMode } from 'src/common/enums';
import { QuestionAndAnswerSection } from 'src/features/room/components/question-and-answer-section';
import { ToolTip } from 'src/common/components';
import { useShouldSeeStartVmExpand } from '../../tasks.helpers';
import { VirtualEnvironmentSetup } from '../virtual-environment-setup';
import { AccordionDetails } from '../accordion-details';
import { ACCORDION_ANIMATION_DURATION } from './accordion.constants';
import { StyledAccordionDetails, StyledAccordionSummary, StyledAccordionSummaryLeft, StyledAccordionSummaryRight, StyledAccordionWrapper, StyledAngledDown, StyledAngledDownWrapper, StyledCheckedCircleIcon, StyledLockedTag, StyledLockedTagLabel, StyledTaskNumber, StyledTaskTitle, StyledUncheckedCircleIcon, StyledLockedTagWrapper } from './accordion.styles';
import { AccordionProps } from './accordion.types';
import { Icon } from './header-icon';
import { ContentButton } from './content-button';
export const Accordion = forwardRef<HTMLDivElement, AccordionProps>(({
  activeMachine,
  attackBoxPrivateIP,
  taskNo,
  _id: taskId,
  title,
  type,
  description,
  activeAccordion,
  handleAccordionChange,
  questions,
  uploadId,
  roomId,
  staticSite,
  joinRoom,
  hasUserJoinedRoom,
  roomCode,
  roomType,
  glossaryData,
  handleTaskContentClick,
  isDesktopOnlyAccessPopupOpen,
  setIsDesktopOnlyAccessPopupOpen,
  hasSeenVmStart,
  cloud,
  hasMultipleVMs,
  isAttackboxWidgetV1ExperimentEnabled,
  isAttackboxWidgetV2ExperimentEnabled,
  autoAnswerQuestions,
  isLocked
}, forwardedRef) => {
  const ref = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [contentReady, setContentReady] = useState(false);
  useImperativeHandle(forwardedRef, () => (ref.current as HTMLDivElement), [ref]);
  const {
    tutorPanelOpen,
    vmPanelOpen
  } = useAppSelector(state => state.splitScreen);
  const isSplitScreenOpen = tutorPanelOpen || vmPanelOpen;
  const shouldSeeVmStart = useShouldSeeStartVmExpand(roomCode, activeAccordion);
  const taskStates = useAppSelector(state => state.room.taskStates);
  const [isTaskClickCompleted, setIsTaskClickCompleted] = useState<boolean>(taskStates[taskNo - 1]?.every(taskState => !!taskState) ?? false);
  const isTaskComplete = useMemo(() => questions.every(question => question.progress?.correct), [questions]);
  useEffect(() => {
    setIsTaskClickCompleted(taskStates[taskNo - 1]?.every(question => !!question) ?? false);
  }, [taskNo, taskStates]);
  const isOpen = useMemo(() => activeAccordion === taskNo, [activeAccordion, taskNo]);

  // Ensures CTA button displays in correct position after accordion opens
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (isOpen) {
      setContentReady(false);
      timer = setTimeout(() => {
        setContentReady(true);
      }, 50);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isOpen]);
  const {
    theme: themeName
  } = useContext(SwitchThemeContext);
  const isDarkMode = themeName === ThemeColorMode.DARK;
  return <StyledAccordionWrapper $isDark={isDarkMode} $isTaskComplete={isTaskComplete} ref={ref}>
        <StyledAccordionSummary onClick={isLocked ? undefined : () => handleAccordionChange(taskNo)} id={`header-${taskNo}`} data-testid={`header-${taskNo}`} role="button" aria-controls={`content-${taskNo}`} aria-expanded={isOpen} tabIndex={taskNo} $splitScreenOpen={isSplitScreenOpen} $isLocked={isLocked}>
          <StyledAccordionSummaryLeft $splitScreenOpen={isSplitScreenOpen}>
            <StyledTaskNumber $isTaskComplete={hasUserJoinedRoom && (isTaskComplete || isTaskClickCompleted)} $splitScreenOpen={isSplitScreenOpen}>
              {' '}
              Task {taskNo}
            </StyledTaskNumber>
            {hasUserJoinedRoom && (isTaskComplete || isTaskClickCompleted) ? <StyledCheckedCircleIcon icon={faCircleCheck} /> : <StyledUncheckedCircleIcon icon={faCircle} />}
            <StyledTaskTitle data-testid={`title-${taskNo}`} dangerouslySetInnerHTML={{
          __html: title
        }} />
          </StyledAccordionSummaryLeft>
          <StyledAccordionSummaryRight>
            <Icon type={type} />
            {isLocked ? <ThemeProvider theme={{
          ...theme,
          colors: darkColors
        }}>
                <ToolTip side="top" $fontSize="tiny" toolTipText="Complete the previous task to unlock this one." sideOffset={0}>
                  <StyledLockedTagWrapper>
                    <StyledLockedTag size="default" $splitScreenOpen={isSplitScreenOpen}>
                      <StyledFlex alignItems="center" gap="0.4rem">
                        <FontAwesomeIcon icon={faLockKeyhole} />
                        <StyledLockedTagLabel>Locked</StyledLockedTagLabel>
                      </StyledFlex>
                    </StyledLockedTag>
                  </StyledLockedTagWrapper>
                </ToolTip>
              </ThemeProvider> : <StyledAngledDownWrapper $splitScreenOpen={isSplitScreenOpen}>
                <StyledAngledDown data-testid={`chevron-${taskNo}`} aria-hidden={!isOpen} $isOpen={isOpen} icon={faAngleDown} />
              </StyledAngledDownWrapper>}
          </StyledAccordionSummaryRight>
        </StyledAccordionSummary>
        <CSSTransition nodeRef={contentRef} in={isOpen} timeout={ACCORDION_ANIMATION_DURATION} classNames="accordion-details" id="accordion-details" onExit={() => {
      if (activeAccordion && activeAccordion < taskNo) {
        return;
      }
      ref?.current?.scrollIntoView?.({
        behavior: 'auto',
        block: 'start',
        inline: 'start'
      });
    }} unmountOnExit={hasUserJoinedRoom}>
          <div ref={contentRef}>
            <StyledAccordionDetails onClick={handleTaskContentClick} role="region" id={`content-${taskNo}`} data-testid={`content-${taskNo}`} aria-labelledby={`header-${taskNo}`} $isTaskComplete={hasUserJoinedRoom && (isTaskComplete || isTaskClickCompleted)} $isDark={isDarkMode}>
              {isAttackboxWidgetV1ExperimentEnabled || isAttackboxWidgetV2ExperimentEnabled ? <>
                  <VirtualEnvironmentSetup roomCode={roomCode} hasSeenVmStart={hasSeenVmStart} task={{
              _id: taskId,
              title,
              description,
              type,
              uploadId,
              staticSite,
              cloud,
              taskNo
            }} hasMultipleVMs={hasMultipleVMs} isAttackboxWidgetV1ExperimentEnabled={isAttackboxWidgetV1ExperimentEnabled} isAttackboxWidgetV2ExperimentEnabled={isAttackboxWidgetV2ExperimentEnabled} />
                  <div>
                    <AccordionDetails glossaryData={glossaryData} description={description} machineIP={activeMachine?.internalIP} attackBoxPrivateIP={attackBoxPrivateIP} shouldSeeVmStart={shouldSeeVmStart} currentAzureLabId={cloud?.azure?.currentLab?.labId} taskType={type} roomCode={roomCode} isAttackboxWidgetV1ExperimentEnabled={isAttackboxWidgetV1ExperimentEnabled} isAttackboxWidgetV2ExperimentEnabled={isAttackboxWidgetV2ExperimentEnabled} taskId={taskId} />
                  </div>
                </> : <div>
                  {contentReady && document.querySelector(`#task-cta-${taskNo}`) ? createPortal(<ContentButton roomCode={roomCode} type={type} taskId={taskId} uploadId={uploadId} roomId={roomId} staticSite={staticSite} taskNo={taskNo} applyDefaultStyles={false} isDesktopOnlyAccessPopupOpen={isDesktopOnlyAccessPopupOpen} setIsDesktopOnlyAccessPopupOpen={setIsDesktopOnlyAccessPopupOpen} hasSeenVmStart={hasSeenVmStart} cloud={cloud} isAttackboxWidgetV1ExperimentEnabled={isAttackboxWidgetV1ExperimentEnabled} isAttackboxWidgetV2ExperimentEnabled={isAttackboxWidgetV2ExperimentEnabled} />,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            document.querySelector(`#task-cta-${taskNo}`)!) : <ContentButton roomCode={roomCode} type={type} taskId={taskId} uploadId={uploadId} roomId={roomId} staticSite={staticSite} taskNo={taskNo} isDesktopOnlyAccessPopupOpen={isDesktopOnlyAccessPopupOpen} setIsDesktopOnlyAccessPopupOpen={setIsDesktopOnlyAccessPopupOpen} hasSeenVmStart={hasSeenVmStart} cloud={cloud} isAttackboxWidgetV1ExperimentEnabled={isAttackboxWidgetV1ExperimentEnabled} isAttackboxWidgetV2ExperimentEnabled={isAttackboxWidgetV2ExperimentEnabled} />}
                  <AccordionDetails glossaryData={glossaryData} description={description} machineIP={activeMachine?.internalIP} attackBoxPrivateIP={attackBoxPrivateIP} shouldSeeVmStart={shouldSeeVmStart} currentAzureLabId={cloud?.azure?.currentLab?.labId} taskType={type} roomCode={roomCode} isAttackboxWidgetV1ExperimentEnabled={isAttackboxWidgetV1ExperimentEnabled} isAttackboxWidgetV2ExperimentEnabled={isAttackboxWidgetV2ExperimentEnabled} taskId={taskId} />
                </div>}
              <QuestionAndAnswerSection questions={questions} roomCode={roomCode} roomType={roomType} taskId={taskId} taskNo={taskNo} joinRoom={joinRoom} hasUserJoinedRoom={hasUserJoinedRoom} isTaskComplete={isTaskComplete} machineIP={activeMachine?.internalIP} attackBoxPrivateIP={attackBoxPrivateIP} autoAnswerQuestions={autoAnswerQuestions} />
            </StyledAccordionDetails>
          </div>
        </CSSTransition>
      </StyledAccordionWrapper>;
});